import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useMediaQuery } from 'usehooks-ts'

import { ButtonProductFilters } from '@entities/Product'

import { useSearchQuery } from '@features/search'
import useSearchModal from '@features/search/hooks/useSearchModal'
import { useAddressStore } from '@features/shipping-methods'


import { ProductPreviewCard } from '@widgets/product-preview'

import { QUERY_NAMES } from '@shared/contants'
import {
  NestedFilterInput,
  Product,
  ProductFilter,
  ProductFiltersQuery,
  SearchProductsFullQuery,
  SearchProductsFullQueryVariables,
  SearchProductsQuery,
  useSearchProductsFullQuery,
} from '@shared/generated'
import { useFilters } from '@shared/hooks'
import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'
import { cn } from '@shared/lib/cn'
import { DynamicFilters } from '@shared/types'
import CardSkeleton from '@shared/ui/card-skeleton'
import TitleWithCount from '@shared/ui/title-with-count'
import getNoun from '@shared/uttils/getNoun'
import { mapFiltersToApi } from '@shared/uttils/mapFiltersToApi'

import Empty from './empty'

import styles from './styles.module.scss'

const LIMIT = 12

function ProductsLoading () {
  return (
    <div className={styles.root}>
      {Array.from({ length: 8 }).map((_, index) => (
        <CardSkeleton key={index} />
      ))}
    </div>
  )
}

interface SearchProductsProps {
  className?: string;
  classNameEmpty?: string;
  onGetFilters: (filters: ProductFiltersQuery['productFilters']) => void;
}

export default function SearchProducts (props: SearchProductsProps) {
  const { closeSearchModal } = useSearchModal()
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { className, onGetFilters } = props
  const { filters } = useFilters<DynamicFilters>({
    groupKey: QUERY_NAMES.PRODUCT_FILTERS,
  })
  const apiFilters = mapFiltersToApi(filters)
  const searchQuery = useSearchQuery()
  const { t: tp } = useTranslation('product')
  const { t: ts } = useTranslation('search')
  const [ availableFilters, setAvailableFilters ] = useState<ProductFilter[]>()
  const { shopId } = useAddressStore()

  const { data, loading, fetchMore } = useSearchProductsFullQuery({
    variables: {
      shopId: shopId,
      pager: {
        page: 1,
        limit: LIMIT,
      },
      searchQuery: {
        search: searchQuery,
      },
      filter: apiFilters as NestedFilterInput,
    },
    skip: !searchQuery,
  })

  useEffect(() => {
    if (data?.searchProducts?.filters) {
      const filters = data.searchProducts.filters
      onGetFilters(filters)
      setAvailableFilters(filters as ProductFilter[])
    }
  }, [ data ])

  const { hasMore, loadingMore, fetchNextPage, clearPagination } =
    useFetchMorePagination<
      SearchProductsFullQuery,
      SearchProductsFullQueryVariables
    >({
      loading,
      fetchMore,
      pageVariable: 'pager',
      usePager: true,
      variables: {
        shopId,
        pager: {
          limit: LIMIT,
        },
        searchQuery: {
          search: searchQuery,
        },
        filter: apiFilters as NestedFilterInput,
      },
      updaterFunction: (cached, fetched) => {
        const prev = cached?.searchProducts?.edges ?? []
        const curr = fetched?.searchProducts?.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            searchProducts: {
              ...cached.searchProducts,
              edges: [ ...prev, ...curr ],
              pageInfo:
                fetched.searchProducts?.pageInfo ||
                cached.searchProducts?.pageInfo,
            },
          } as SearchProductsQuery,
        }
      },
    })

  useEffect(() => {
    clearPagination()
  }, [ filters, searchQuery, clearPagination ])

  useEffect(() => {
    if (!isTablet) {
      closeSearchModal()
    }
  }, [ isTablet ])

  const items = data?.searchProducts?.edges || []

  const total = data?.searchProducts?.pageInfo?.total ?? 0

  if (!loading && items.length === 0 && total === 0) {
    return <Empty />
  }

  return (
    <div className={cn(styles.wrapper)}>
      {searchQuery && (
        <TitleWithCount
          className={styles.title}
          title={ts('notEmpty.title', {
            query: searchQuery,
          })}
          key={`${searchQuery}-${items.length}-${total}`}
        >
          {apiFilters?.and?.length
            ? `${items.length} ${getNoun(items.length, tp('product.1'), tp('product.2'), tp('product.3'))}`
            : `${total ?? 0} ${getNoun(total ?? 0, tp('product.1'), tp('product.2'), tp('product.3'))}`}
        </TitleWithCount>
      )}

      {loading && <ProductsLoading />}

      {isTablet && (
        <div className={styles['group']}>
          <ButtonProductFilters
            className={styles['root__filter-button']}
            externalFilters={availableFilters}
          />
        </div>
      )}

      <InfiniteScroll
        dataLength={items.length}
        next={fetchNextPage}
        hasMore={hasMore}
        loader={loadingMore ? <ProductsLoading /> : null}
      >
        <section className={cn(styles.root, className)}>
          {items.map((p) => (
            <ProductPreviewCard
              key={p?.id}
              className={styles.card}
              data={p as Product}
            />
          ))}
        </section>
      </InfiniteScroll>
    </div>
  )
}
